import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home, Layers, Activity, BarChart3, Users, Grid3x3, Settings,
  ShieldCheck, Zap, FileText, ChevronDown, LogOut,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/store/auth';
import { useSoftphone } from '@/store/softphone';
import { Agent, logActivityBeacon } from '@/lib/api';
import clsx from 'clsx';

type Role = 'agent' | 'leader' | 'admin';

interface NavSpec {
  to: string;
  icon: LucideIcon;
  label: string;
  dot?: boolean;
}

const ROLE_LABEL: Record<Role, string> = {
  agent: 'Müşteri\nTemsilcisi',
  leader: 'Takım\nLideri',
  admin: 'Admin',
};

const ROLE_NAVS: Record<Role, NavSpec[]> = {
  agent: [
    { to: '/', icon: Home, label: 'Çalışma\nAlanı', dot: true },
    { to: '/activity', icon: Activity, label: 'Aktivite' },
    { to: '/performance', icon: BarChart3, label: 'Performans' },
    { to: '/settings', icon: Settings, label: 'Ayarlarım' },
  ],
  leader: [
    { to: '/', icon: Home, label: 'Yönetim\nPaneli' },
    { to: '/agents', icon: Users, label: 'Temsilciler' },
    { to: '/queue-status', icon: Layers, label: 'Kuyruk\nDurumu' },
    { to: '/reports', icon: BarChart3, label: 'Raporlar' },
  ],
  admin: [
    { to: '/admin/users', icon: Users, label: 'Kullanıcılar' },
    { to: '/admin/speed-dials', icon: Zap, label: 'Kısa\nYollar' },
    { to: '/admin/crm-templates', icon: FileText, label: 'CRM\nŞablonları' },
  ],
};

/**
 * ArkConnect sidebar — referansa uygun:
 *  - Üstte rol seçici (büyük ikon kutucuk + 2-satırlık label + chevron)
 *    Click → role cycle (agent → leader → admin) [admin yetkisi gerekli]
 *  - Nav items (rol bazlı menü)
 *  - Altta profile (avatar + "Profilim")
 */
export function Sidebar() {
  const { user, logout } = useAuth();

  // Çıkış — kuyruktaysa önce kuyruktan çıkar (yoksa 3CX login kalıp çağrı
  // yönlendirmeye devam eder) + online (Bağlı) segmentini kapat (agent_offline) +
  // lokal queue state'i sıfırla. Eskiden topbar'daydı, buraya taşındı.
  const handleLogout = async () => {
    if (useSoftphone.getState().queueIn) {
      try { await Agent.setQueue(false); } catch { /* best-effort */ }
    }
    logActivityBeacon('agent_offline', { reason: 'logout' });
    useSoftphone.setState({ queueIn: false, breakMode: false, currentBreakReason: null });
    await logout();
  };
  // Sadece adminler rol önizleme yapabilir (agent↔leader↔admin görünümleri gez).
  // Gerçek leader kullanıcısı sabit leader nav görür, cycle edemez.
  const canCycle = user?.role === 'admin';
  const [role, setRole] = useState<Role>(
    user?.role === 'admin' ? 'admin' : user?.role === 'leader' ? 'leader' : 'agent',
  );
  const items = ROLE_NAVS[role];

  const cycleRole = () => {
    if (!canCycle) return;
    const order: Role[] = ['agent', 'leader', 'admin'];
    const idx = order.indexOf(role);
    setRole(order[(idx + 1) % order.length]);
  };

  return (
    <aside
      className="arkchrome w-[78px] flex flex-col overflow-y-auto flex-shrink-0"
      role="navigation"
      aria-label="Ana navigasyon"
    >
      {/* Role selector */}
      <div className="px-1.5 pt-2.5 pb-2 border-b border-white/5">
        <button
          onClick={cycleRole}
          disabled={!canCycle}
          className="w-full bg-surface border border-white/5 rounded-[10px] py-1.5 px-1 flex flex-col items-center gap-0.5 shadow-sm transition hover:bg-brand-50 hover:border-brand-200 disabled:hover:bg-surface disabled:hover:border-white/5 disabled:cursor-default"
        >
          <div className="w-[26px] h-[26px] rounded-[7px] bg-brand-100 text-brand-700 flex items-center justify-center mb-0.5">
            <RoleIcon role={role} />
          </div>
          <div className="text-[9px] font-semibold leading-tight text-slate-700 whitespace-pre-line">
            {ROLE_LABEL[role]}
          </div>
          {canCycle && (
            <ChevronDown size={10} className="text-slate-400" />
          )}
        </button>
      </div>

      {/* Nav — menüler dikeyde ortalı (justify-center) */}
      <nav className="flex-1 px-1.5 py-1.5 flex flex-col gap-px justify-center">
        {items.map((item) => <NavItem key={item.to} {...item} />)}
      </nav>

      {/* Çıkış (bottom) — Profilim kaldırıldı, topbar'daki çıkış buraya taşındı */}
      <div className="border-t border-white/5 px-1.5 py-1.5">
        <button
          onClick={handleLogout}
          title="Çıkış"
          className="w-full py-1.5 px-1 rounded-[7px] flex flex-col items-center gap-1 text-slate-600 hover:bg-red-50 hover:text-red-600 transition"
        >
          <LogOut size={18} strokeWidth={2} />
          <span className="text-[9.5px] font-medium">Çıkış</span>
        </button>
      </div>
    </aside>
  );
}

function NavItem({ to, icon: Icon, label, dot }: NavSpec) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      title={label.replace('\n', ' ')}
      className={({ isActive }) =>
        clsx(
          'relative w-full pt-1.5 pb-1.5 px-1 rounded-[7px] flex flex-col items-center gap-1 transition',
          isActive
            ? 'bg-surface text-brand-700 shadow-sm'
            : 'text-slate-600 hover:bg-surface/50 hover:text-slate-900',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-brand-500 rounded-r" />}
          <Icon size={18} strokeWidth={2} />
          <span className="text-[9.5px] font-medium leading-[1.1] text-center whitespace-pre-line">{label}</span>
          {dot && (
            <span className="absolute top-1.5 right-3 w-1.5 h-1.5 rounded-full bg-brand-500 ring-2 ring-base" />
          )}
        </>
      )}
    </NavLink>
  );
}

function RoleIcon({ role }: { role: Role }) {
  if (role === 'admin') return <ShieldCheck size={14} />;
  if (role === 'leader') return <Users size={14} />;
  return <Users size={14} />; // agent default
}
